import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ConversationChangedEvent, SocialCommentChangedEvent } from "@multi-chat/shared";
import {
  EVENTS_COALESCE_MS,
  openInboxEvents,
  type InboxEventsConnection,
} from "../../api/inbox-events-api.js";
import { getInboxUnreadSummary } from "../../api/inbox-api.js";
import { useOrganizations } from "../organizations/organization-context.js";
import {
  INBOX_EVENTS_COALESCE_MAX_MS,
  planInboxEventCoalesce,
  shouldResyncInboxOnEventsOpen,
} from "./inbox-event-coalesce.js";

type InboxRealtimeListener = (event: ConversationChangedEvent) => void;
type SocialCommentRealtimeListener = (event: SocialCommentChangedEvent) => void;
type InboxReconnectListener = () => void;

type InboxRealtimeContextValue = {
  /** Null until the first successful summary fetch for the selected organization. */
  unreadConversationCount: number | null;
  subscribe: (listener: InboxRealtimeListener) => () => void;
  subscribeSocialCommentChanged: (listener: SocialCommentRealtimeListener) => () => void;
  /** Fired on EventSource reconnect, not on the first open for this organization. */
  subscribeReconnect: (listener: InboxReconnectListener) => () => void;
};

const InboxRealtimeContext = createContext<InboxRealtimeContextValue | null>(null);

/**
 * Owns the one organization-scoped Inbox EventSource for the authenticated shell.
 * useInbox subscribes here and must not open a second connection (C-7c / D11).
 */
export function InboxRealtimeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { selected } = useOrganizations();
  const organizationId = selected?.id ?? null;

  const [unreadConversationCount, setUnreadConversationCount] = useState<number | null>(null);
  const listenersRef = useRef(new Set<InboxRealtimeListener>());
  const socialCommentListenersRef = useRef(new Set<SocialCommentRealtimeListener>());
  const reconnectListenersRef = useRef(new Set<InboxReconnectListener>());
  const summaryOrgIdRef = useRef<number | null>(null);
  const summaryAbortRef = useRef<AbortController | null>(null);

  const refreshUnreadSummary = useCallback(async (orgId: number): Promise<void> => {
    summaryAbortRef.current?.abort();
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    try {
      const response = await getInboxUnreadSummary(orgId, controller.signal);
      if (controller.signal.aborted || summaryOrgIdRef.current !== orgId) {
        return;
      }
      setUnreadConversationCount(response.unreadConversationCount);
    } catch {
      if (controller.signal.aborted || summaryOrgIdRef.current !== orgId) {
        return;
      }
      // Keep last count only for the same organization (never flash 0 on failure).
    } finally {
      if (summaryAbortRef.current === controller) {
        summaryAbortRef.current = null;
      }
    }
  }, []);

  const onOrganizationChange = useEffectEvent((orgId: number | null) => {
    summaryAbortRef.current?.abort();
    summaryAbortRef.current = null;
    summaryOrgIdRef.current = orgId;
    setUnreadConversationCount(null);
  });

  useEffect(() => {
    onOrganizationChange(organizationId);
  }, [organizationId]);

  useEffect(() => {
    if (organizationId == null) {
      return;
    }

    const boundOrgId = organizationId;
    let connection: InboxEventsConnection | null = null;
    let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
    let coalesceStartedAt: number | null = null;
    let dirty = false;
    let hasOpened = false;

    const flush = (): void => {
      coalesceTimer = null;
      coalesceStartedAt = null;
      if (!dirty || summaryOrgIdRef.current !== boundOrgId) {
        dirty = false;
        return;
      }
      dirty = false;
      void refreshUnreadSummary(boundOrgId);
    };

    const schedule = (): void => {
      dirty = true;
      const decision = planInboxEventCoalesce({
        now: Date.now(),
        startedAt: coalesceStartedAt,
        trailingMs: EVENTS_COALESCE_MS,
        maxWaitMs: INBOX_EVENTS_COALESCE_MAX_MS,
      });
      coalesceStartedAt = decision.startedAt;
      if (decision.flushNow) {
        if (coalesceTimer) {
          clearTimeout(coalesceTimer);
          coalesceTimer = null;
        }
        flush();
        return;
      }
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      coalesceTimer = setTimeout(flush, decision.delayMs);
    };

    connection = openInboxEvents(boundOrgId, {
      onOpen: () => {
        if (summaryOrgIdRef.current !== boundOrgId) {
          return;
        }
        const reconnecting = shouldResyncInboxOnEventsOpen(hasOpened);
        hasOpened = true;
        void refreshUnreadSummary(boundOrgId);
        if (!reconnecting) {
          return;
        }
        for (const listener of reconnectListenersRef.current) {
          listener();
        }
      },
      onConversationChanged: (event) => {
        if (summaryOrgIdRef.current !== boundOrgId) {
          return;
        }
        if (event.organizationId !== boundOrgId) {
          return;
        }
        for (const listener of listenersRef.current) {
          listener(event);
        }
        schedule();
      },
      onSocialCommentChanged: (event) => {
        if (summaryOrgIdRef.current !== boundOrgId) {
          return;
        }
        if (event.organizationId !== boundOrgId) {
          return;
        }
        for (const listener of socialCommentListenersRef.current) {
          listener(event);
        }
      },
    });

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "visible") return;
      if (summaryOrgIdRef.current !== boundOrgId) return;
      void refreshUnreadSummary(boundOrgId);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      connection?.close();
    };
  }, [organizationId, refreshUnreadSummary]);

  const subscribe = useCallback((listener: InboxRealtimeListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const subscribeSocialCommentChanged = useCallback((listener: SocialCommentRealtimeListener) => {
    socialCommentListenersRef.current.add(listener);
    return () => {
      socialCommentListenersRef.current.delete(listener);
    };
  }, []);

  const subscribeReconnect = useCallback((listener: InboxReconnectListener) => {
    reconnectListenersRef.current.add(listener);
    return () => {
      reconnectListenersRef.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({
      unreadConversationCount,
      subscribe,
      subscribeSocialCommentChanged,
      subscribeReconnect,
    }),
    [unreadConversationCount, subscribe, subscribeSocialCommentChanged, subscribeReconnect],
  );

  return (
    <InboxRealtimeContext.Provider value={value}>{children}</InboxRealtimeContext.Provider>
  );
}

export function useInboxRealtime(): InboxRealtimeContextValue {
  const value = useContext(InboxRealtimeContext);
  if (!value) {
    throw new Error("useInboxRealtime must be used within InboxRealtimeProvider");
  }
  return value;
}

/** Safe for rail/header when the provider may be absent in isolated tests. */
export function useInboxUnreadConversationCount(): number | null {
  const value = useContext(InboxRealtimeContext);
  return value?.unreadConversationCount ?? null;
}
